import React, { useEffect, useState, useRef } from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { generateUrl } from '../services';

interface YouMightLikeProps {
    loading?: boolean;
    cardClick?: (id: string) => void;
    areas: {
        name: string,
        photos: string[]
    }[]
}

const YouMightLike = ({ loading, cardClick, areas }: YouMightLikeProps) => {
    const [activeAreaPhoto, setActiveAreaPhoto] = useState({});
    const scrollRef = useRef<HTMLDivElement>(null);

    const scrollSection = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300;
            scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (loading || !areas.length) return; // Don't start interval if loading
        
        const interval = setInterval(() => {
            setActiveAreaPhoto(prev => {
                const newState = {};
                areas.forEach((area, idx) => {
                    const currentIdx = prev[idx] || 0;
                    newState[idx] = (currentIdx + 1) % area.photos.length;
                });
                return newState;
            });
        }, 5000);
        
        return () => clearInterval(interval);
    }, [areas, loading]);

    if (loading) {
        return (
            <div className="mb-2">
                {/* Skeleton for header */}
                <div className="flex justify-between items-center pb-2">
                    <div className="h-6 w-48 bg-gray-200 rounded-md ml-2 animate-pulse"></div>
                    <div className="flex gap-2">
                        <div className="p-2 rounded-full bg-gray-200 border border-gray-200 animate-pulse">
                            <FaChevronLeft className="text-transparent" />
                        </div>
                        <div className="p-2 rounded-full bg-gray-200 border border-gray-200 animate-pulse">
                            <FaChevronRight className="text-transparent" />
                        </div>
                    </div>
                </div>
                
                {/* Skeleton for cards container */}
                <div className="flex gap-2 overflow-x-auto px-2 pb-2 scrollbar-hide">
                    {[...Array(3)].map((_, idx) => (
                        <div key={idx} className="flex-shrink-0 w-64 h-80 relative rounded-md overflow-hidden">
                            {/* Main image skeleton */}
                            <div className="w-full h-full bg-gray-200 animate-pulse"></div>
                            
                            {/* Gradient overlay skeleton */}
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-300/50 via-gray-200/30 to-transparent" />
                            
                            {/* Content skeleton */}
                            <div className="absolute bottom-0 left-0 right-0 p-4">
                                {/* Title skeleton */}
                                <div className="h-7 w-3/4 bg-gray-300 rounded-md animate-pulse mb-2"></div>
                                
                                {/* Dots skeleton */}
                                <div className="flex gap-1.5 mt-2">
                                    {[...Array(4)].map((_, dotIdx) => (
                                        <div 
                                            key={dotIdx}
                                            className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const hasAreas = areas && areas.length > 0;

    if (!loading && !hasAreas) {
    return (
        <div className="mb-2 px-2">
        <div className="h-5 w-40 bg-gray-100 rounded-md" />
            <div className="w-64 h-32 rounded-md bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                Nothing to show yet. Please check connection and retry.
            </div>
        <div className="mt-2 h-28 w-full bg-gray-100 rounded-md" />
        </div>
        );
    }



    return (
        <div className="mb-2">
            <div className="flex justify-between items-center pb-2">
                <h2 className="text-lg ml-2 font-bold poppins-semibold text-[#bf491e]">You Might Like in</h2>
                <div className="flex gap-2">
                    <button
                        onClick={() => scrollSection('left')}
                        className="p-2 rounded-full bg-white border border-[#FFDDA6] hover:bg-[#FFF8E7] transition-colors"
                    >
                        <FaChevronLeft className="text-[#5E503F]" />
                    </button>
                    <button
                        onClick={() => scrollSection('right')}
                        className="p-2 rounded-full bg-white border border-[#FFDDA6] hover:bg-[#FFF8E7] transition-colors"
                    >
                        <FaChevronRight className="text-[#5E503F]" />
                    </button>
                </div>
            </div>
            <div className="flex gap-2 overflow-x-auto px-2 pb-2 scrollbar-hide" ref={scrollRef}>
                {areas && areas.length > 0 && areas.map((area, idx) => (
                    <div key={idx} className="flex-shrink-0 w-64 h-80 relative rounded-md overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow" onClick={() => cardClick && cardClick(area.name)}>
                        <img 
                            src={generateUrl(area.photos[activeAreaPhoto[idx] || 0])} 
                            alt={area.name}
                            className="w-full h-full object-cover transition-opacity duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                            <h3 className="text-white text-2xl font-semibold">{area.name}</h3>
                            <div className="flex gap-1.5 mt-2">
                                {area.photos.map((_, photoIdx) => (
                                    <div 
                                        key={photoIdx}
                                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                                            photoIdx === (activeAreaPhoto[idx] || 0) ? 'bg-white w-4' : 'bg-white/50'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default YouMightLike;